/**
 * `clause-matches` — FR-28, NFR-06, CR-01 · CLM-34.
 *
 * The **read side of the clause matcher**: the queue a reviewer works through.
 * `match-clauses` writes `contract_clause.confidence_band`; this lists the rows
 * that need a person (`pending_match_review` — the MEDIUM band) or that are
 * candidate new library clauses (`bespoke_candidates` — the LOW band with no
 * clause); `manage-clause resolve_clause_match` records the decision.
 *
 * ## Scoped, unlike the two library queues
 *
 * `clause-candidates` and `clauses` are corpus-level: the cluster and library
 * tables carry no party anchor. These rows are **paragraphs of specific
 * contracts**, so the statement carries the scope predicate anchored on
 * `root_contract_key` (NFR-06, §8.1 Rule 4) and `page.total_in_scope` and the
 * two `totals` are real, scoped figures. A practice-scoped reviewer sees their
 * practice's rows; an out-of-scope `contract_key` filter returns an empty page,
 * never a 403 (S4 precedent — no existence oracle).
 *
 * ## Only the two matcher queues are bound here
 *
 * `CLAUSE_REVIEW_QUEUES` (`manage-clause.ts`) also names `suggested_aliases` and
 * `stale_judgements`. Those are Phase 5 ground (CLM-39/CLM-40) and answer `400`
 * here — the request schema extracts exactly the two this lane writes.
 *
 * ## Excerpts, not text columns
 *
 * `paragraph_excerpt` is `full_text` sliced by the row's span, capped at 2,000
 * characters, so the surface can show the paragraph beside the candidate's
 * `master_text_excerpt` without a second fetch. It is a slice of a column the
 * caller could already read under the same predicate — not a copy of the
 * document (rule 13's concern is a model echoing text; a reviewer reading it is
 * the point).
 */
import { z } from 'zod';
import { ContractClauseSchema } from '../domain/clause.js';
import { PageInfoSchema, PageRequestSchema } from '../pagination.js';
import { ClauseCodeSchema, ContractKeySchema, VersionNoSchema } from '../primitives.js';
import { ContractTypeConfirmationSchema } from '../vocabulary.js';
import { ClauseReviewQueueSchema } from './manage-clause.js';
/* -------------------------------------------------------------------------- */
/* GET /v1/clause-matches                                                      */
/* -------------------------------------------------------------------------- */
/** The two queues the matcher writes. The other two members of the enum are 400 here. */
export const ClauseMatchQueueSchema = ClauseReviewQueueSchema.extract(['pending_match_review', 'bespoke_candidates']);
export const ClauseMatchListRequestSchema = z
    .object({
    queue: ClauseMatchQueueSchema,
    /** Narrow to one contract. Out of scope ⇒ empty page, not 403. */
    contract_key: ContractKeySchema.optional(),
    page: PageRequestSchema.optional(),
})
    .strict();
/** The library clause the matcher proposed, if any. `null` for a bespoke row. */
export const ClauseMatchCandidateSchema = z.object({
    clause_code: ClauseCodeSchema,
    clause_version_no: VersionNoSchema,
    title: z.string().min(1).max(200),
    master_text_excerpt: z.string().max(2000),
});
export const ClauseMatchListItemSchema = ContractClauseSchema.extend({
    /** `full_text.slice(start_offset, end_offset)`, capped at 2,000 characters. */
    paragraph_excerpt: z.string().max(2000),
    candidate: ClauseMatchCandidateSchema.nullable(),
    /** `contract.contract_type` (0005:194) — so the queue can say "SOW" beside the row. */
    contract_type: z.string().min(1),
    /** CLM-110 (R-110-23) — rendered beside `contract_type` as its `provisional` tag. */
    contract_type_confirmation: ContractTypeConfirmationSchema,
    /** `contract.sow_number` (0005:393); null for an MSA or NDA. */
    sow_number: z.string().nullable(),
});
export const ClauseMatchListResponseSchema = z.object({
    items: z.array(ClauseMatchListItemSchema),
    page: PageInfoSchema,
    /** Both **scoped**: counts of rows the caller may read, within the same filters. */
    totals: z.object({
        pending_match_review: z.number().int().min(0),
        bespoke_candidates: z.number().int().min(0),
    }),
});
//# sourceMappingURL=clause-matches.js.map